import type { ReactNode } from 'react';
import { useUiLabels } from '../labels';
import { cn } from '../lib/cn';

/** Placeholder in the shape of the content that is loading. */
export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden className={cn('animate-pulse rounded-sm bg-pressed', className)} />;
}

/** Widths of placeholder lines, so a block of text does not look like a bar chart. */
const lineWidths = ['w-3/4', 'w-1/2', 'w-2/3', 'w-5/12', 'w-7/12'];

function width(index: number) {
  return lineWidths[index % lineWidths.length];
}

/**
 * Wrapper for a skeleton that stands for a whole region: marks it busy and announces "Lädt …"
 * once, so the bars themselves stay silent.
 */
export function SkeletonRegion({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  const labels = useUiLabels();
  return (
    <div aria-busy className={className}>
      <span className="sr-only" role="status">
        {labels.loading}
      </span>
      {children}
    </div>
  );
}

/** Lines of running text. */
export function SkeletonText({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <div className={cn('flex flex-col gap-2.5 py-0.5', className)}>
      {Array.from({ length: lines }, (_, index) => (
        <Skeleton
          key={index}
          className={cn('h-3.5', index === lines - 1 && lines > 1 ? 'w-2/3' : 'w-full')}
        />
      ))}
    </div>
  );
}

/** Same layout as `PageHeader`: breadcrumbs, title, description or meta, actions on the right. */
export function PageHeaderSkeleton({
  breadcrumbs = false,
  icon = false,
  description = true,
  meta = false,
  actions = 0,
}: {
  breadcrumbs?: boolean;
  icon?: boolean;
  description?: boolean;
  meta?: boolean;
  /** Number of buttons on the right. */
  actions?: number;
}) {
  return (
    <div aria-hidden className="flex flex-col gap-2 pb-5">
      {breadcrumbs && <Skeleton className="my-0.5 h-3 w-40" />}
      <div className="flex items-start gap-3">
        {icon && <Skeleton className="mt-0.5 size-9 shrink-0 rounded-md" />}
        <div className="flex min-w-0 flex-1 flex-col">
          <Skeleton className="my-1 h-6 w-56 max-w-full md:h-7 md:w-72" />
          {description && <Skeleton className="mt-2 h-3.5 w-96 max-w-full" />}
          {meta && (
            <div className="mt-2.5 flex gap-3">
              <Skeleton className="h-5 w-16 rounded-full" />
              <Skeleton className="h-5 w-24" />
            </div>
          )}
        </div>
        {actions > 0 && (
          <div className="flex shrink-0 gap-2">
            {Array.from({ length: actions }, (_, index) => (
              <Skeleton
                key={index}
                className={cn('size-control rounded-md', index === actions - 1 && 'md:w-32')}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Title line of a card, as in `CardHeader` and `SettingsSection`. */
function CardTitleSkeleton() {
  return (
    <div className="px-4 pt-4 md:px-5">
      <Skeleton className="my-1 h-4 w-40" />
    </div>
  );
}

/** Card with label/value pairs, as `Card` with `DescriptionList` on detail pages. */
export function DescriptionListSkeleton({
  rows = 5,
  title = true,
  className,
}: {
  rows?: number;
  title?: boolean;
  className?: string;
}) {
  return (
    <div aria-hidden className={cn('rounded-lg border border-line bg-surface', className)}>
      {title && <CardTitleSkeleton />}
      <div className="grid gap-x-6 gap-y-4 p-4 md:grid-cols-[minmax(8rem,auto)_1fr] md:px-5">
        {Array.from({ length: rows }, (_, index) => (
          <div key={index} className="contents">
            <Skeleton className="my-0.5 h-3.5 w-24" />
            <Skeleton className={cn('-mt-2 my-0.5 h-3.5 md:mt-0.5', width(index))} />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Card of settings, as `SettingsSection` with `SettingsRow`s: text left, control right. */
export function SettingsSectionSkeleton({
  rows = 3,
  className,
}: {
  rows?: number;
  className?: string;
}) {
  return (
    <div aria-hidden className={cn('rounded-lg border border-line bg-surface', className)}>
      <CardTitleSkeleton />
      <div className="divide-y divide-line px-4 md:px-5">
        {Array.from({ length: rows }, (_, index) => (
          <div
            key={index}
            className="flex flex-col gap-3 py-4 md:flex-row md:items-center md:gap-6"
          >
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <Skeleton className="h-3.5 w-32" />
              <Skeleton className={cn('h-3 max-w-80', width(index + 1))} />
            </div>
            <Skeleton
              className={cn(
                'h-control shrink-0 rounded-md',
                index % 3 === 1 ? 'w-11 rounded-full md:h-6' : 'w-40 md:w-48',
              )}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Card with form fields: label above, input below, as `Card` with `FormField`s. */
export function FormCardSkeleton({
  fields = 4,
  columns = 1,
  className,
}: {
  fields?: number;
  /** Two columns from the tablet on, as forms with short fields have them. */
  columns?: 1 | 2;
  className?: string;
}) {
  return (
    <div aria-hidden className={cn('rounded-lg border border-line bg-surface', className)}>
      <CardTitleSkeleton />
      <div className={cn('grid gap-4 p-4 md:px-5', columns === 2 && 'md:grid-cols-2')}>
        {Array.from({ length: fields }, (_, index) => (
          <div key={index} className="flex flex-col gap-2">
            <Skeleton className={cn('h-3.5', index % 2 === 0 ? 'w-28' : 'w-36')} />
            <Skeleton className="h-control w-full rounded-md" />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Card with a list of entries: optional icon, two lines, optional badge on the right. Fits
 * "Für mich", search hits, notifications, attachments, history.
 */
export function ListSkeleton({
  rows = 3,
  title = true,
  icon = false,
  trailing = false,
  card = true,
  className,
}: {
  rows?: number;
  title?: boolean;
  icon?: boolean;
  /** Badge or button at the end of each entry. */
  trailing?: boolean;
  /** Without frame, inside an existing card or panel. */
  card?: boolean;
  className?: string;
}) {
  return (
    <div aria-hidden className={cn(card && 'rounded-lg border border-line bg-surface', className)}>
      {title && card && <CardTitleSkeleton />}
      <ul className={cn('flex flex-col gap-1', card && 'p-4 md:px-5')}>
        {Array.from({ length: rows }, (_, index) => (
          <li key={index} className="flex items-center gap-3 py-1.5">
            {icon && <Skeleton className="size-5 shrink-0 rounded-md" />}
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <Skeleton className={cn('h-3.5', width(index))} />
              <Skeleton className="h-3 w-1/3" />
            </div>
            {trailing && <Skeleton className="h-5 w-16 shrink-0 rounded-full" />}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Card with a heading and a block of content of the given height class, e.g. a map. */
export function CardSkeleton({
  className,
  bodyClassName = 'h-32',
  title = true,
}: {
  className?: string;
  bodyClassName?: string;
  title?: boolean;
}) {
  return (
    <div aria-hidden className={cn('rounded-lg border border-line bg-surface', className)}>
      {title && <CardTitleSkeleton />}
      <div className="p-4 md:px-5">
        <Skeleton className={cn('w-full rounded-md', bodyClassName)} />
      </div>
    </div>
  );
}

/**
 * Whole detail page while the record loads: header with icon and status, a main card with
 * fields and the side column, in the grid of `DetailPage`.
 */
export function DetailPageSkeleton({ aside = true }: { aside?: boolean }) {
  return (
    <SkeletonRegion className="flex min-w-0 flex-col">
      <PageHeaderSkeleton breadcrumbs icon meta description={false} actions={2} />
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem] xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="flex min-w-0 flex-col gap-6">
          <DescriptionListSkeleton rows={6} />
          <ListSkeleton rows={2} icon />
        </div>
        {aside && (
          <div className="flex min-w-0 flex-col gap-6">
            <ListSkeleton rows={2} trailing />
            <ListSkeleton rows={2} icon />
          </div>
        )}
      </div>
    </SkeletonRegion>
  );
}
